/*
Copyright (C) 2017  Cloudbase Solutions SRL
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import { observer } from "mobx-react";
import React from "react";
import styled from "styled-components";
import { Plus } from "lucide-react";

import { ProviderTypes } from "@src/@types/Providers";
import EndpointLogos from "@src/components/modules/EndpointModule/EndpointLogos";
import { providerLabel } from "@src/components/modules/EndpointModule/EndpointLogos/ProviderMark";
import {
  ChoiceCard,
  LoadingBlock,
  SelectedCheck,
  T9,
} from "@src/components/modules/WizardModule/WizardKit";
import Dropdown from "@src/components/ui/Dropdowns/Dropdown";

import type { Endpoint } from "@src/@types/Endpoint";

// The source / target cloud picker: one Ops card per provider, the provider's logo on top and
// the endpoint dropdown (or "Add endpoint") underneath. The card of the chosen cloud is brand.
const Wrapper = styled.div<any>`
  display: flex;
  flex-direction: column;
  width: 100%;
`;
const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 16px;
  padding-bottom: 8px;
`;
const Card = styled(ChoiceCard)`
  align-items: stretch;
`;
const LogoArea = styled.div<{ clickable?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 96px;
  padding: 0 16px;
  border-bottom: 1px solid ${T9.slate200};
  ${props => (props.clickable ? "cursor: pointer;" : "")}
`;
const CardFooter = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 10px 16px 16px 16px;
`;
const Count = styled.div<{ none?: boolean }>`
  font-size: 12px;
  line-height: 16px;
  color: ${props => (props.none ? T9.slate400 : T9.slate500)};
  white-space: nowrap;
`;
const AddButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 228px;
  height: 34px;
  border-radius: 6px;
  border: 1px dashed ${T9.slate300};
  background: var(--t9-bg-surface);
  color: ${T9.slate700};
  font: inherit;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  &:hover {
    border-color: ${T9.brand300};
    color: ${T9.brand600};
    background: ${T9.brand50};
  }
  &:focus-visible {
    outline: 2px solid ${T9.accent500};
    outline-offset: 2px;
  }
`;
const Empty = styled.div`
  padding: 48px 16px;
  text-align: center;
  font-size: 14px;
  color: ${T9.slate500};
`;

type Props = {
  providers: ProviderTypes[];
  endpoints: Endpoint[];
  loading: boolean;
  selectedEndpoint?: Endpoint | null;
  otherEndpoint?: Endpoint | null;
  onChange: (endpoint: Endpoint) => void;
  onAddEndpoint: (provider: ProviderTypes) => void;
};
@observer
class WizardEndpointList extends React.Component<Props> {
  handleOnChange(selectedItem: Endpoint | null, provider: ProviderTypes) {
    if (selectedItem && selectedItem.id !== "addNew") {
      this.props.onChange(selectedItem);
      return;
    }

    this.props.onAddEndpoint(provider);
  }

  renderProvider(provider: ProviderTypes) {
    const otherEndpoint = this.props.otherEndpoint;
    const endpoints = this.props.endpoints.filter(
      e => e.type === provider && (!otherEndpoint || otherEndpoint.id !== e.id),
    );
    const selectedItem =
      this.props.selectedEndpoint &&
      this.props.selectedEndpoint.type === provider
        ? this.props.selectedEndpoint
        : null;
    const count = endpoints.length;
    let actionInput = null;
    if (count > 0) {
      const items: any[] = [
        ...endpoints,
        { id: "addNew", name: "Add new endpoint…", provider },
      ];

      actionInput = (
        <Dropdown
          primary={Boolean(selectedItem)}
          items={items}
          valueField="id"
          labelField="name"
          noSelectionMessage="Select an endpoint"
          width={228}
          centered
          selectedItem={selectedItem}
          onChange={item => {
            if (item.id === "addNew") {
              this.handleOnChange(null, item.provider);
            } else {
              this.handleOnChange(item, item.provider);
            }
          }}
        />
      );
    } else {
      actionInput = (
        <AddButton
          type="button"
          data-test-id={`wizardEndpointList-add-${provider}`}
          onClick={() => {
            this.props.onAddEndpoint(provider);
          }}
        >
          <Plus size={16} />
          Add endpoint
        </AddButton>
      );
    }

    return (
      <Card
        key={provider}
        selected={Boolean(selectedItem)}
        data-test-id={`wizardEndpointList-card-${provider}`}
      >
        {selectedItem ? <SelectedCheck /> : null}
        <LogoArea
          title={providerLabel(provider)}
          clickable={count === 1 && !selectedItem}
          onClick={() => {
            // One endpoint for this cloud: clicking the logo picks it.
            if (count === 1 && !selectedItem) {
              this.props.onChange(endpoints[0]);
            }
          }}
        >
          <EndpointLogos height={64} endpoint={provider} />
        </LogoArea>
        <CardFooter>
          <Count none={count === 0}>
            {count === 0
              ? "No endpoints yet"
              : `${count} endpoint${count === 1 ? "" : "s"}`}
          </Count>
          {actionInput}
        </CardFooter>
      </Card>
    );
  }

  renderProviders() {
    if (this.props.loading) {
      return null;
    }
    if (this.props.providers.length === 0) {
      return <Empty>No clouds can be used here.</Empty>;
    }

    return (
      <Grid>
        {this.props.providers.map(provider => this.renderProvider(provider))}
      </Grid>
    );
  }

  render() {
    return (
      <Wrapper>
        {this.props.loading ? <LoadingBlock label="Loading clouds…" /> : null}
        {this.renderProviders()}
      </Wrapper>
    );
  }
}

export default WizardEndpointList;
